import * as Sentry from "@sentry/react";

import { parseClientConfigJsonSafely } from "~/lib/helpers";
import { isAbortedRequest } from "~/lib/shared";
import { StoreState } from "~/redux/reducers";

import { warehouseApi } from "./warehouseApi";

export type ClientConfigResponse = {
  clientConfiguration: string | null; // JSON string (camelCase - backend uses JsonNamingPolicy.CamelCase)
  etag?: string; // ETag header for concurrency control
};

export type ClientConfigCreateRequest = {
  ClientConfiguration: string; // JSON string of config
};

export type ClientConfigUpdateRequest = {
  ClientConfiguration: string; // JSON string of config
  etag: string; // Required ETag for updates
};

export const clientConfigApi = warehouseApi.injectEndpoints({
  endpoints: (build) => ({
    getClientConfig: build.query<ClientConfigResponse, void>({
      query: () => ({
        url: `/configuration/client-frontend`,
        method: "GET"
      }),
      transformResponse: (
        response: { clientConfiguration: string | null; updatedUtc?: string },
        meta
      ) => {
        // Extract ETag from response headers for concurrency control
        const etag = meta?.response?.headers.get("etag");

        return {
          clientConfiguration: response.clientConfiguration,
          etag: etag || undefined
        };
      },
      providesTags: ["client config"],
      async onQueryStarted(_arg, { queryFulfilled, ...rest }) {
        const { improvedErrorHandlingConfigEnabled } = (
          rest.getState() as StoreState
        ).app;
        if (!improvedErrorHandlingConfigEnabled) return;

        try {
          const { data } = await queryFulfilled;
          const hadConfigurationString = Boolean(data?.clientConfiguration);
          const rawParsed = parseClientConfigJsonSafely(data);
          if (rawParsed === null && hadConfigurationString) {
            Sentry.captureMessage(
              "client config: configuration JSON did not parse",
              { level: "warning" }
            );
          }
        } catch (rejection: unknown) {
          const queryError = (rejection as { error?: unknown }).error;
          if (isAbortedRequest(queryError)) {
            return;
          }
          const { clientConfigError } = (rest.getState() as StoreState).app;
          Sentry.captureMessage("client config: GET request failed", {
            level: "warning",
            extra: { message: clientConfigError }
          });
        }
      }
    }),
    createClientConfig: build.mutation<void, ClientConfigCreateRequest>({
      query: (config) => ({
        url: `/configuration/client-frontend`,
        method: "POST",
        body: config
      }),
      invalidatesTags: ["client config"]
    }),
    updateClientConfig: build.mutation<void, ClientConfigUpdateRequest>({
      query: ({ etag, ...config }) => ({
        url: `/configuration/client-frontend`,
        method: "POST",
        body: config,
        headers: {
          "If-Match": etag
        }
      }),
      invalidatesTags: ["client config"]
    })
  }),
  overrideExisting: false
});
